import { createFileRoute, notFound } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getWorkspaceIdWithFallback } from "@/lib/routing/workspace-context";
import { searchWorkspaceDealsServer } from "@/server/deals";
import DealDetailSheet from "@/components/deals/detail/sheet";
import { useNavigate } from "@tanstack/react-router";
import type { DealTableItem } from "@/components/deals/table/types";
import type { DealStatusCode } from "@/components/deals/schema";

export const Route = createFileRoute("/workspace/$workspaceSlug/deals/$dealId")({
  loader: async ({ context, parentMatchPromise, params }) => {
    const workspaceId = await getWorkspaceIdWithFallback(
      context,
      "deals.detail",
      parentMatchPromise,
    );

    try {
      // Use search API to get full deal info with company, contact, etc.
      const searchResult = await searchWorkspaceDealsServer({
        data: {
          workspaceId,
          request: {
            // Search by deal ID to get the specific deal
            search_query: params.dealId,
            page: 1,
            page_size: 1,
          },
        },
      });

      if (!searchResult.items || searchResult.items.length === 0) {
        throw notFound();
      }

      const searchItem = searchResult.items[0];

      // Convert DealSearchItemResponse to DealTableItem format
      const deal: DealTableItem = {
        id: searchItem.id,
        dealName: searchItem.name,
        companyId: searchItem.company?.id,
        companyName: searchItem.company?.name ?? "Unknown",
        companyLogoUrl: searchItem.company?.logo_url ?? undefined,
        value: parseFloat(searchItem.value_amount),
        valueCurrency: searchItem.value_currency,
        contact: {
          id: searchItem.contact?.id ?? "",
          name:
            `${searchItem.contact?.first_name || ""} ${searchItem.contact?.last_name || ""}`.trim() ||
            String(searchItem.contact?.email || "—"),
          avatarUrl: undefined,
        },
        lastMessageAuthor: searchItem.contact
          ? {
              id: searchItem.contact.id,
              name:
                `${searchItem.contact.first_name || ""} ${searchItem.contact.last_name || ""}`.trim() ||
                String(searchItem.contact?.email),
              avatarUrl: undefined,
            }
          : undefined,
        lastMessagePreview: searchItem.last_message?.subject ?? "-",
        stage: searchItem.stage as DealStatusCode,
        campaignId: searchItem.campaign?.id ?? undefined,
        campaignName: searchItem.campaign?.name ?? "-",
        createdAt: searchItem.created_at,
        createdBy: {
          id: searchItem.owner?.id ?? "",
          name: searchItem.owner
            ? `${searchItem.owner.first_name || ""} ${searchItem.owner.last_name || ""}`.trim() ||
              String(searchItem.owner.email)
            : "—",
          avatarUrl: undefined, // Search API doesn't provide owner avatar
          role: "user",
        },
      };

      return {
        workspaceId,
        deal,
      };
    } catch {
      // If deal not found, throw notFound
      throw notFound();
    }
  },
  component: DealDetailPage,
});

function DealDetailPage() {
  const { t } = useTranslation(["pages", "common"]);
  const { workspaceId, deal } = Route.useLoaderData();
  const navigate = useNavigate();

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header with back button */}
      <div className="h-[64px] px-6 border-b border-[#dfdfdf]">
        <div className="flex h-full items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              navigate({
                to: "/workspace/$workspaceSlug/deals",
                params: { workspaceSlug: workspaceId },
              })
            }
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("common:back")}
          </Button>
          <h1 className="text-[16px] font-semibold text-gray-900">{deal.dealName}</h1>
        </div>
      </div>

      {/* Deal detail content - using the existing DealDetailSheet component */}
      <div className="flex-1 overflow-auto">
        <DealDetailSheet
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              navigate({
                to: "/workspace/$workspaceSlug/deals",
                params: { workspaceSlug: workspaceId },
              });
            }
          }}
          deal={deal}
          workspaceId={workspaceId}
        />
      </div>
    </div>
  );
}
